import React, {useEffect, useState} from "react";
import {useSearchParams, useNavigate} from 'react-router-dom';
import {useDataWithRoute, useTitleFromComponent} from "../../../utils/hooks";
import {useVsiAppContext} from "../../../stores/VsiApp";
import {VsiBubble, VsiH4, VsiH5} from "../../../styled/App.styled";
import {Card,} from "reactstrap";
import Grid from "../../../components/Grid";
import VsiLabeledSelect from "../../../components/VsiLabeledSelect";
import {
    storageOverUtil,
    storageOverProvisioned,
    serversNearThresh,
    serversOverThresh,
    clustersOverCap
} from "../../../utils/gridColumnDefs/businessUnits/itDashboard";
import {toSnakeCase} from "../../../utils/Helpers";

const allDashboards = [
    {
        value: 'storage_over_util',
        label: 'Storage Over Utilization',
        label2: 'Pools Over Provisioned',
        route: 'business_unit_storage_over_utilization_proc'
    },
    {
        value: 'storage_over_thresh',
        label: 'Servers Over Threshold',
        label2: 'Servers Approaching Threshold',
        route: 'business_unit_storage_over_threshold_proc'
    },
    {
        value: 'clusters_over_cap',
        label: 'Virtual Clusters Over Capacity',
        route: 'business_unit_clusters_over_capacity_proc'
    },
];

export default function ITDashboard({title, context}) {
    useTitleFromComponent(`${context} / ${title}`);
    const { theme, client, periodId } = useVsiAppContext();
    const [searchParams, setSearchParams] = useSearchParams();
    const [secondGrid, setSecondGrid] = useState(false);
    let navigate = useNavigate();
    const searchUnit = searchParams.get('dashboard');
    let defaultUnit = allDashboards[0];
    if (searchUnit) {
        defaultUnit = allDashboards.find(({value}) => searchParams.get('dashboard') === value) || defaultUnit;
    }

    const returnActiveDef = (unit) => {
        switch (unit) {
            case 'storage_over_thresh':
                return [serversOverThresh, serversNearThresh];
            case 'clusters_over_cap':
                return [clustersOverCap, []];
            default:
                return [storageOverUtil, storageOverProvisioned];
        }
    };

    const [unit, setUnit] = useState(defaultUnit);
    const [activeDef, setActiveDef] = useState(returnActiveDef(defaultUnit.value) || []);

    const [data, {isLoading}] = useDataWithRoute(`/api/v6/report/${unit.route}?client_id=${client.clientId}&period_id=${periodId}`);
    const [filteredGridOneData, setFilteredGridOneData] = useState([]);
    const [filteredGridTwoData, setFilteredGridTwpData] = useState([]);

    function filterCollision(array) {
        if (array.length === 0) {
            return array
        } else if (!array[0].hasOwnProperty('used') || !array[0].hasOwnProperty('allocated')) {
            return array
        } else {
            return array.filter(item => Math.abs(item['used'] - item['allocated']) > 0.01);
        }
    }

    useEffect(() => {
        if (unit.value !== 'clusters_over_cap') {
            setSecondGrid(true);
        }
        setFilteredGridOneData(filterCollision(data?.recordsets?.[0] ?? []))
        setFilteredGridTwpData(filterCollision(data?.recordsets?.[1] ?? []))
        setActiveDef(returnActiveDef(unit.value));
    }, [data, searchParams]);

    const onUnitChange = (selectedDash) => {
        setUnit(selectedDash);
        if (selectedDash.value === 'clusters_over_cap') {
            setSecondGrid(false);
        } else {
            setSecondGrid(true);
        }
        setSearchParams({
            dashboard: selectedDash.value,
        }, {replace: true});
    };

    const onRowDblClick = (e) => {
        if (unit.value === "storage_over_util") {
            const source = e?.data?.source?.toLowerCase?.();
            if (source === "datastore") {
                navigate(`/reports/virtual/datastore/datastore-summary?datastore_name=${encodeURIComponent(e.data.resource_name)}&vcenter_name=${encodeURIComponent(e.data.device_name)}&period_id=${periodId}`);
            } else if (source === "pool") {
                navigate(`/reports/storage/device/device-trends?device_name=${encodeURIComponent(e.data.device_name)}&period_id=${periodId}&metric=by_pool&pool=${encodeURIComponent(e?.data?.resource_name ?? e?.data?.datastore_name)}`)
            }
        } else if (unit.value === "storage_over_thresh") {
            navigate(`/reports/virtual/host/host-summary?esx_host_name=${encodeURIComponent(e.data.esx_host_name)}&vcenter_name=${encodeURIComponent(e.data.server_name)}&cluster_name=${encodeURIComponent(e.data.cluster_name)}&period_id=${periodId}`)

        } else if (unit.value === "clusters_over_cap") {
            navigate(`/reports/virtual/cluster/cluster-summary?cluster_name=${encodeURIComponent(e.data.cluster_name)}&vcenter_name=${encodeURIComponent(e.data.vcenter_name)}&period_id=${periodId}`)
        }
    }

    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>IT Dashboard</VsiH4>
            <VsiBubble noAutobot>
                <VsiLabeledSelect options={allDashboards}
                                  isLoading={isLoading}
                                  minWidth={300}
                                  label={'Select Unit'}
                                  onChange={onUnitChange}
                                  defaultValue={unit}/>
            </VsiBubble>
            <VsiH5>{unit.label}</VsiH5>
            <VsiBubble autobotName={`${unit?.label?.split(" ").join("")}Grid`} unit={unit.value}>
                <Grid addStyle data={filteredGridOneData}
                          rowDoubleClicked={onRowDblClick}
                      saveParams={{unit: unit.value, metric1: toSnakeCase(unit.label)}}
                      columnDefs={activeDef[0]} isLoading={isLoading}/>
            </VsiBubble>
            {secondGrid ? (<>
                <VsiH5>{unit.label2}</VsiH5>
                <VsiBubble autobotName={`${unit?.label2?.split(" ").join("")}Grid`} unit={unit.value}>
                    <Grid addStyle columnDefs={activeDef[1]}
                          rowDoubleClicked={onRowDblClick}
                          saveParams={{unit: unit.value, metric2: toSnakeCase(unit.label2)}}
                          data={filteredGridTwoData} isLoading={isLoading}/>
                </VsiBubble>
            </>) : null}
        </Card>
    )
}
